import React, { useEffect, useState } from 'react';
import { Button, DrawerProps, Spin, Table, Tag, message } from 'antd';
import { antdDrawer, create, useModal } from '@ebay/nice-modal-react';
import { CustomDrawer } from '@isocel/front-common';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';

import { Invoice, InvoiceDetailsRow } from '../../queries/api/types';
import { getInvoiceDetails } from '../../queries/api/invoice';

import './HistoriqueFacturation.less';

export interface InvoiceDetailsDrawerData {
    invoice: Invoice;
}

type Props = DrawerProps & { data: InvoiceDetailsDrawerData };

export const formatDate = (value?: string | null, withTime = false) => {
    if (!value) return '';
    return moment(value).format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');
};

const InvoiceDetailsDrawer = create<Props>(({ data, ...props }) => {
    const modal = useModal();
    const { invoice } = data;

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<InvoiceDetailsRow[]>([]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getInvoiceDetails(invoice._id)
            .then((response) => {
                if (cancelled) return;
                setRows(response.details ?? []);
            })
            .catch(() => {
                if (cancelled) return;
                message.error('Erreur lors du chargement du détail de la facture.');
                setRows([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [invoice._id]);

    const columns: ColumnsType<InvoiceDetailsRow> = [
        {
            title: 'Magasin',
            key: 'store',
            render: (_: unknown, r) => `${r.storeCode || ''} - ${r.storeName || ''}`.trim().replace(/^-\s*/, ''),
        },
        {
            title: 'Code article',
            dataIndex: 'packageEan',
            key: 'packageEan',
        },
        {
            title: 'Code Support',
            dataIndex: 'packageInternCode',
            key: 'packageInternCode',
        },
        {
            title: 'Libellé Support',
            key: 'packageLabel',
            render: (_: unknown, r) => {
                const t = r.packageTypeName || '';
                const m = r.packageModelName || '';
                if (t && m && t !== m) return `${t} + ${m}`;
                return t || m || '';
            },
        },
        {
            title: 'Quantité à facturer',
            dataIndex: 'value',
            key: 'value',
            align: 'right',
            render: (value: number) => {
                const safe = Number.isFinite(value) ? value : 0;
                const cls = safe < 0 ? 'invoice-details-quantity negative' : 'invoice-details-quantity positive';
                const display = safe < 0 ? `- ${Math.abs(safe)}` : `${safe}`;
                return <span className={cls}>{display}</span>;
            },
        },
        {
            title: 'Prix unitaire',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            align: 'right',
            render: (value?: number) =>
                value != null ? `${value.toFixed(2)} €` : <span style={{ color: '#aaa' }}>—</span>,
        },
        {
            title: 'Code TVA',
            dataIndex: 'tvaCode',
            key: 'tvaCode',
            render: (value?: string) => value || <span style={{ color: '#aaa' }}>—</span>,
        },
    ];

    return (
        <CustomDrawer
            width={900}
            title={`Détails de la facture ${invoice.name || invoice._id}`}
            className="invoice-details-drawer"
            {...props}
            {...antdDrawer(modal)}
            onClose={() => {
                modal.hide().catch(() => undefined);
            }}
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 16 }}>
                <div className="info-item">
                    <b>Nom :</b> {invoice.name || invoice._id}
                </div>
                <div className="info-item">
                    <b>Émission :</b> {formatDate(invoice.createdAt, true)}
                </div>
                <div className="info-item">
                    <b>Période :</b> {formatDate(invoice.startDate)} – {formatDate(invoice.endDate)}
                </div>
                <div className="info-item">
                    <b>Utilisateur :</b> {invoice.userName || invoice.userId || '-'}
                </div>
                {invoice.status && (
                    <div className="info-item">
                        <b>Statut :</b> <Tag>{invoice.status}</Tag>
                    </div>
                )}
            </div>

            <h3>Détail des lignes facturées :</h3>
            {loading ? (
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey={(r, idx) => `${r._id || String(idx)}`}
                    pagination={false}
                    size="small"
                    bordered
                />
            )}

            <div style={{ marginTop: 16 }}>
                <Button
                    type="primary"
                    onClick={() => {
                        modal.hide().catch(() => undefined);
                    }}
                >
                    Retour
                </Button>
            </div>
        </CustomDrawer>
    );
});

export default InvoiceDetailsDrawer;
